import { Flex, useDisclosure, Box } from '@chakra-ui/core'
import { CreatorReceiverTimeDisplay } from 'components/Post/PostHeader/CreatorReceiverTimeDisplay'
import { PostMenu } from 'components/Post/PostHeader/PostMenu'
import { PostPin } from 'components/Post/PostHeader/PostPin'
import { usePinnedPosts } from 'components/Post/postHooks/usePinedPosts'
import { getPostsIdsFromLocalStorage } from 'components/Post/utils'
import { RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { ChevronLeft } from 'lucide-react'
import React, { memo, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Post } from 'redux/reducers/feed'
import { PostModal } from '../PostModal'
import { isRtlSelector } from 'redux/selectors/general'

interface IProps {
  post: Post
  onClose: () => void
}
export const PostModalHeader = memo(({ post, onClose }: IProps) => {
  const {
    created_by,
    created_at_ts,
    post_id,
    type,
    title,
    post_origin_users,
    users,
    groups,
    tags,
    post_origin_group_names,
    is_manager_pin,
    is_self_pin,
    is_new,
    is_new_version,
  } = post
  const isRtl = useSelector(isRtlSelector)
  const pinedPostsObject = getPostsIdsFromLocalStorage()
  const { posts } = useSelector((state: RootState) => state.feed)
  const { permission } = useSelector((state: RootState) => state.config.config)
  const myUID = useSelector((state: RootState) => state.auth.uid)
  const isNewPost = is_new || is_new_version

  const editRole = created_by === myUID
  const isAdminPostPermission = permission?.posts === keys.USER_ACTIONS_MAP.APP_ADMIN

  const { isOpen: isEditPostModalOpen, onOpen: onEditPostModalOpen, onClose: onCloseEditPostModal } = useDisclosure()

  const { handlePinPost } = usePinnedPosts({
    myUID: myUID!,
    pin_created_at_ts: post.pin_created_at_ts,
    created_at_ts: post.created_at_ts,
    post_id: post.post_id,
    groups: post.groups,
    users: post.users,
    tags: post.tags ?? [],
    is_manager_pin: post.is_manager_pin,
    posts,
    isSearchPage: false,
  })

  const isShowPinMenuItem = useMemo(
    () =>
      isNewPost &&
      !is_manager_pin &&
      (pinedPostsObject[myUID!]?.includes(post_id) ||
        pinedPostsObject?.[myUID!]?.length < 2 ||
        !pinedPostsObject?.[myUID!]),
    [isNewPost, is_manager_pin, myUID, pinedPostsObject, post_id]
  )

  const isShowPinManagerMenuItem = useMemo(
    () =>
      (editRole &&
        isNewPost &&
        !is_self_pin &&
        !posts.some((p) => p.created_by === myUID && p.is_manager_pin === true)) ||
      is_manager_pin,
    [editRole, isNewPost, is_manager_pin, is_self_pin, myUID, posts]
  )

  const canShowPostMenu = useMemo(
    () => editRole || isShowPinMenuItem || isShowPinManagerMenuItem || isAdminPostPermission,
    [editRole, isShowPinMenuItem, isShowPinManagerMenuItem, isAdminPostPermission]
  )

  return (
    <Box cursor="pointer" py="10px">
      <Flex justifyContent="space-between">
        <Flex style={{ columnGap: '10px' }} alignItems="center">
          <ChevronLeft
            onClick={onClose}
            cursor="pointer"
            size={20}
            style={{ transform: isRtl ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
          <CreatorReceiverTimeDisplay
            {...{
              created_by,
              postOriginUsers: post_origin_users,
              users,
              postType: type,
              title,
              created_at_ts,
              postId: post_id,
              postGroupName: post_origin_group_names,
              tags,
            }}
          />
        </Flex>

        <PostPin {...{ post_id, is_manager_pin, isNewPost, pinedPostsObject }} />

        {!(is_manager_pin && !editRole) && canShowPostMenu ? (
          <PostMenu
            isGroup={keys.POST_TYPE.GROUP_POST === type}
            {...{
              groups,
              users,
              tags,
              is_manager_pin,
              pinedPostsObject,
              post_id,
              handlePinPost,
              onEditPost: onEditPostModalOpen,
              editRole,
              isAdminPostPermission,
              isShowPinMenuItem,
              isShowPinManagerMenuItem,
            }}
          />
        ) : null}
      </Flex>
      {isEditPostModalOpen && (
        <PostModal
          isEditing={true}
          isOpen={isEditPostModalOpen}
          onClose={onCloseEditPostModal}
          postType={post.type}
          postTitle={post.title}
          postRichText={post.rich_text}
          {...post}
        />
      )}
    </Box>
  )
})
